/**
 * configurator.js
 * Generic product configurator state engine.
 *
 * All configuration (layer labels, options, fabric groups) comes from
 * the #ConfiguratorProductData JSON injected by main-product-configurator.liquid.
 * Nothing is hardcoded — this file works for any product using the template.
 *
 * State flow:
 *   Layer 1 selection (e.g. Environment) → filters visible fabric groups
 *   Layer 2 selection (e.g. Wood)        → affects render lookup key
 *   Fabric group selection               → shows colour chips + fabric info
 *   Colour chip selection                → resolves variant, swaps render, enables ATC
 *
 * Render URL pattern: {cdnBase}{fabricSlug}_{colourSlug}_{layer2Slug}.jpg
 * Close-up URL pattern: {cdnBase}{fabricSlug}_{colourSlug}_closeup.jpg
 * Wood swatch pattern:  {cdnBase}wood_{layer2Slug}.jpg
 */

class ProductConfigurator {
  constructor() {
    this.section = document.querySelector(".cfg-product");
    if (!this.section) return;

    this.data = this.loadData();
    if (!this.data) return;

    // ---- Parse layer options from comma-separated strings ----
    this.layer1Options = this.parseOptions(this.data.layer1Options);
    this.layer2Options = this.parseOptions(this.data.layer2Options);

    // ---- State ----
    this.state = {
      layer1: null,
      layer2: null,
      fabricSlug: null,
      colourSlug: null,
      variantId: null,
    };

    // ---- Build fabric group map from variant metafield data ----
    this.fabricGroups = this.buildFabricGroups();

    // ---- Cache DOM refs ----
    this.cacheDOM();

    // ---- Render dynamic pills ----
    this.renderLayer1Pills();
    this.renderLayer2Pills();
    this.renderFabricPills();

    // ---- Init hero carousel ----
    this.initHeroSwiper();

    // ---- Bind all events ----
    this.bindEvents();

    // ---- Info block: accordion + nav scroll ----
    this.initInfoAccordion();
    this.initInfoNav();

    // ---- Default to "All" for layer 1 ----
    this.setLayer1("all");

    // ---- Pre-select first option in each group ----
    this._initialising = true;
    this.preselectDefaults();
    this._initialising = false;
  }

  /* =====================================================================
     DEFAULT PRESELECTION
  ===================================================================== */

  preselectDefaults() {
    // Layer 2: first wood option
    if (this.layer2Options.length) {
      this.setLayer2(this.slugify(this.layer2Options[0]));
    }

    // Fabric: first visible fabric group
    const firstFabric = this.fabricGroups.values().next().value;
    if (firstFabric) {
      this.selectFabric(firstFabric.fabricSlug);

      // Colour: first colour in that fabric
      if (firstFabric.colours.length) {
        const c = firstFabric.colours[0];
        this.selectColour(c.colourSlug, c.variantId, c.price);
      }
    }
  }

  /* =====================================================================
     DATA LOADING
  ===================================================================== */

  loadData() {
    const el = document.getElementById("ConfiguratorProductData");
    if (!el) {
      console.error("[Configurator] #ConfiguratorProductData not found.");
      return null;
    }

    let raw = el.textContent;

    // Safety net: replace any "Liquid error: ..." values that may appear
    // if a Shopify metafield filter fails (e.g. image_url on an unset file metafield).
    // These are plain strings in the JSON output and will break JSON.parse().
    // This regex finds any JSON value that starts with "Liquid err" and replaces it with null.
    raw = raw.replace(/"Liquid err[^"]*"/g, "null");

    try {
      return JSON.parse(raw);
    } catch (e) {
      console.error("[Configurator] JSON parse error:", e);
      // Log first 800 chars so the developer can see exactly what's malformed
      console.error(
        "[Configurator] Raw JSON (truncated):",
        raw.substring(0, 800),
      );
      return null;
    }
  }

  /**
   * Parses a comma-separated string of options into a trimmed array.
   * "Beach, Town, Country" → ["Beach", "Town", "Country"]
   */
  parseOptions(str) {
    if (!str) return [];
    return str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  /**
   * Builds a Map: fabricSlug → fabric group object with colours array.
   * Groups all variants by fabricSlug so we show one pill per fabric family.
   */
  buildFabricGroups() {
    const groups = new Map();

    this.data.variants.forEach((v) => {
      if (!v.fabricSlug) return;

      if (!groups.has(v.fabricSlug)) {
        groups.set(v.fabricSlug, {
          fabricSlug: v.fabricSlug,
          fabricName: v.fabricName,
          environment: v.environment, // maps to layer1 value
          maker: v.maker,
          description: v.description,
          performance: v.performance,
          care: v.care,
          composition: v.composition,
          colours: [],
        });
      }

      groups.get(v.fabricSlug).colours.push({
        colourSlug: v.colourSlug,
        colourName: v.colourName,
        swatchImage: v.swatchImage,
        variantId: v.id,
        price: v.price,
        available: v.available,
      });
    });

    return groups;
  }

  /* =====================================================================
     DOM CACHING
  ===================================================================== */

  cacheDOM() {
    // Panel open/close
    this.openBtn = document.getElementById("cfg-open-panel");
    this.closeBtn = document.getElementById("cfg-close-panel");
    this.panel = document.getElementById("cfg-panel");
    this.overlay = document.getElementById("cfg-overlay");

    // Layer 1
    this.layer1PillsEl = document.getElementById("cfg-layer1-pills");

    // Layer 2
    this.layer2PillsEl = document.getElementById("cfg-layer2-pills");

    // Fabric pills
    this.fabricPillsContainer = document.getElementById("cfg-fabric-pills");
    this.fabricEmptyState = document.getElementById("cfg-fabric-empty");

    // Fabric detail
    this.fabricDetail = document.getElementById("cfg-fabric-detail");
    this.colourChipsEl = document.getElementById("cfg-colour-chips");
    this.fabricDescEl = document.getElementById("cfg-fabric-description");
    this.fabricPerfEl = document.getElementById("cfg-fabric-performance");
    this.fabricCareEl = document.getElementById("cfg-fabric-care");
    this.fabricCompEl = document.getElementById("cfg-fabric-composition");

    // Fabric info tabs
    this.fabricTabs = document.querySelectorAll(".cfg-panel__fabric-tab");
    this.fabricTabPanels = document.querySelectorAll(
      ".cfg-panel__fabric-tab-panel",
    );

    // Summary
    this.summaryLayer1Value = document.getElementById(
      "cfg-summary-layer1-value",
    );
    this.summarySwatches = document.getElementById("cfg-summary-swatches");
    this.summaryRenderImg = document.getElementById("cfg-summary-render");
    this.summaryPrice = document.getElementById("cfg-summary-price");
    this.viewRenderBtn = document.getElementById("cfg-view-render");
    this.zoomBtn = document.getElementById("cfg-zoom-btn");

    // Hero swiper container (for render swap on active slide)
    this.heroSwiperEl = document.getElementById("cfg-hero-swiper");

    // Page price (below title on hero)
    this.pagePrice = document.getElementById("cfg-price");

    // ATC form
    this.atcBtn = document.getElementById("cfg-atc-btn");
    this.cartForm = document.getElementById("cfg-cart-form");
    this.variantInput = document.getElementById("cfg-variant-input");
    this.propLayer1 = document.getElementById("cfg-prop-layer1");
    this.propLayer2 = document.getElementById("cfg-prop-layer2");
    this.propFabric = document.getElementById("cfg-prop-fabric");
    this.propColour = document.getElementById("cfg-prop-colour");
    this.propMaterialSwatch = document.getElementById("cfg-prop-material-swatch");
    this.propFabricSwatch = document.getElementById("cfg-prop-fabric-swatch");
    this.propRenderImage = document.getElementById("cfg-prop-render-image");

    // Full screen
    this.fullscreenEl = document.getElementById("cfg-fullscreen");
    this.fullscreenCloseBtn = document.getElementById("cfg-fullscreen-close");
    this.fullscreenRender = document.getElementById("cfg-fullscreen-render");

    // Bottom bar nav links
    this.navLinks = document.querySelectorAll("[data-nav-link]");
  }

  /* =====================================================================
     HERO SWIPER CAROUSEL
  ===================================================================== */

  initHeroSwiper() {
    const swiperEl = document.getElementById("cfg-hero-swiper");
    const paginationEl = document.getElementById("cfg-pagination");
    if (!swiperEl || typeof Swiper === "undefined") return;

    this.heroSwiper = new Swiper(swiperEl, {
      direction: "horizontal",
      loop: false,
      slidesPerView: 1,
      speed: 300,
      pagination: {
        el: paginationEl,
        clickable: true,
        bulletClass: "cfg-product__hero-dot",
        bulletActiveClass: "cfg-product__hero-dot--active",
      },
    });
  }

  /* =====================================================================
     LAYER 1 PILLS (e.g. Environment — filter layer)
  ===================================================================== */

  renderLayer1Pills() {
    if (!this.layer1PillsEl) return;
    this.layer1PillsEl.innerHTML = "";

    // "All" pill first
    const allPill = this.makePill("All", "all", false);
    allPill.addEventListener("click", () => this.setLayer1("all"));
    this.layer1PillsEl.appendChild(allPill);

    this.layer1Options.forEach((opt) => {
      const slug = this.slugify(opt);
      const pill = this.makePill(opt, slug, false);
      pill.addEventListener("click", () => this.setLayer1(slug));
      this.layer1PillsEl.appendChild(pill);
    });
  }

  setLayer1(slug) {
    if (this.state.layer1 === slug) return;
    this.state.layer1 = slug;

    // Update active pill
    this.layer1PillsEl?.querySelectorAll(".cfg-l1-pill").forEach((p) => {
      const isActive = slug && p.dataset.value === slug;
      p.classList.toggle("active", isActive);
      p.setAttribute("aria-pressed", String(isActive));
    });

    // Update summary label
    const label = !slug
      ? "—"
      : slug === "all"
        ? "All"
        : this.layer1Options.find((o) => this.slugify(o) === slug) || slug;
    if (this.summaryLayer1Value) this.summaryLayer1Value.textContent = label;

    // Reset fabric/colour on environment change
    this.state.fabricSlug = null;
    this.state.colourSlug = null;
    this.state.variantId = null;

    this.renderFabricPills();

    // Auto-select first available fabric + colour
    this._initialising = true;
    const firstPill = this.fabricPillsContainer?.querySelector('.cfg-fabric-pill');
    if (firstPill) {
      this.selectFabric(firstPill.dataset.fabricSlug);
      const group = this.fabricGroups.get(firstPill.dataset.fabricSlug);
      if (group && group.colours.length) {
        const c = group.colours[0];
        this.selectColour(c.colourSlug, c.variantId, c.price);
      }
    } else {
      this.hideFabricDetail();
      this.hideRender();
      this.updateSummarySwatches();
      this.updateAtcBtn();
    }
    this._initialising = false;
  }

  /* =====================================================================
     LAYER 2 PILLS (e.g. Wood — finish selector)
  ===================================================================== */

  renderLayer2Pills() {
    if (!this.layer2PillsEl) return;
    this.layer2PillsEl.innerHTML = "";

    this.layer2Options.forEach((opt) => {
      const slug = this.slugify(opt);
      const pill = this.makePill(opt, slug, false);
      pill.classList.add("cfg-l2-pill");
      pill.addEventListener("click", () => this.setLayer2(slug));
      this.layer2PillsEl.appendChild(pill);
    });
  }

  setLayer2(slug) {
    if (this.state.layer2 === slug) return;
    this.state.layer2 = slug;

    this.layer2PillsEl?.querySelectorAll(".cfg-l2-pill").forEach((p) => {
      const isActive = slug && p.dataset.value === slug;
      p.classList.toggle("active", isActive);
      p.setAttribute("aria-pressed", String(isActive));
    });

    // Re-render the render if a colour is already selected
    if (this.state.variantId) {
      this.updateRender();
    }

    this.updateSummarySwatches();
  }

  /* =====================================================================
     FABRIC PILLS (filtered by layer 1 selection)
  ===================================================================== */

  renderFabricPills() {
    if (!this.fabricPillsContainer) return;

    const layer1 = this.state.layer1;

    // Filter fabric groups by layer1 value (environment field on variant)
    const filtered = [];
    this.fabricGroups.forEach((group) => {
      if (layer1 === "all" || this.slugify(group.environment) === layer1) {
        filtered.push(group);
      }
    });

    // Remove old fabric pills only (leave empty-state element)
    this.fabricPillsContainer
      .querySelectorAll(".cfg-fabric-pill")
      .forEach((p) => p.remove());

    if (filtered.length === 0) {
      this.fabricEmptyState?.style.removeProperty("display");
      return;
    }

    if (this.fabricEmptyState) this.fabricEmptyState.style.display = "none";

    filtered.forEach((group) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "variant-pill cfg-pill cfg-fabric-pill caption-plaid";
      btn.dataset.fabricSlug = group.fabricSlug;
      btn.dataset.value = group.fabricSlug;
      btn.textContent = group.fabricName;
      btn.setAttribute(
        "aria-pressed",
        String(this.state.fabricSlug === group.fabricSlug),
      );

      if (this.state.fabricSlug === group.fabricSlug)
        btn.classList.add("active");

      btn.addEventListener("click", () => this.selectFabric(group.fabricSlug));
      this.fabricPillsContainer.appendChild(btn);
    });
  }

  /* =====================================================================
     FABRIC SELECTION
  ===================================================================== */

  selectFabric(fabricSlug) {
    if (this.state.fabricSlug === fabricSlug) return;

    this.state.fabricSlug = fabricSlug;
    this.state.colourSlug = null;
    this.state.variantId = null;

    // Hide the old render — it no longer applies to the new fabric
    this.hideRender();

    // Update fabric pill active state
    this.fabricPillsContainer
      ?.querySelectorAll(".cfg-fabric-pill")
      .forEach((p) => {
        const isActive = p.dataset.fabricSlug === fabricSlug;
        p.classList.toggle("active", isActive);
        p.setAttribute("aria-pressed", String(isActive));
      });

    const group = this.fabricGroups.get(fabricSlug);
    if (!group) return;

    this.showFabricDetail(group);

    // Auto-select first colour in the new fabric
    if (group.colours.length) {
      const c = group.colours[0];
      this.selectColour(c.colourSlug, c.variantId, c.price);
    }

    this.updateSummarySwatches();
    this.updateAtcBtn();

    // Scroll panel to bottom after expand animation starts (skip during initial preselection)
    if (!this._initialising) {
      setTimeout(() => {
        const isMobile = window.matchMedia('(max-width: 1023px)').matches;
        const scrollContainer = isMobile
          ? this.colourChipsEl?.closest('.cfg-panel')
          : this.colourChipsEl?.closest('.cfg-panel__left');
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
        }
      }, 150);
    }
  }

  showFabricDetail(group) {
    if (!this.fabricDetail) return;

    // Colour chips
    if (this.colourChipsEl) {
      this.colourChipsEl.innerHTML = "";

      group.colours.forEach((colour) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "cfg-colour-chip";
        btn.dataset.colourSlug = colour.colourSlug;
        btn.setAttribute("aria-label", colour.colourName);
        btn.setAttribute("aria-pressed", "false");

        // Swatch image tile
        const imgDiv = document.createElement("div");
        imgDiv.className = "cfg-colour-chip__image";
        if (colour.swatchImage) {
          imgDiv.style.backgroundImage = `url(${colour.swatchImage})`;
        } else {
          imgDiv.style.backgroundColor = "#ccc";
        }
        btn.appendChild(imgDiv);

        // Colour name label
        const label = document.createElement("span");
        label.className = "cfg-colour-chip__label caption-plaid";
        label.textContent = colour.colourName;
        btn.appendChild(label);

        btn.addEventListener("click", () => {
          this.selectColour(colour.colourSlug, colour.variantId, colour.price);
        });

        this.colourChipsEl.appendChild(btn);
      });
    }

    // Populate info tabs
    if (this.fabricDescEl)
      this.fabricDescEl.innerHTML = group.description || "";
    if (this.fabricPerfEl)
      this.fabricPerfEl.innerHTML = group.performance || "";
    if (this.fabricCareEl) this.fabricCareEl.innerHTML = group.care || "";
    if (this.fabricCompEl)
      this.fabricCompEl.textContent = group.composition || "";

    // Show the detail section
    this.fabricDetail.removeAttribute("aria-hidden");

    // Reset to description tab
    this.setFabricTab("description");
  }

  hideFabricDetail() {
    if (!this.fabricDetail) return;
    this.fabricDetail.setAttribute("aria-hidden", "true");
  }

  /* =====================================================================
     COLOUR SELECTION
  ===================================================================== */

  selectColour(colourSlug, variantId, price) {
    this.state.colourSlug = colourSlug;
    this.state.variantId = variantId;

    // Update chip active states
    this.colourChipsEl?.querySelectorAll(".cfg-colour-chip").forEach((chip) => {
      const isActive = chip.dataset.colourSlug === colourSlug;
      chip.classList.toggle("active", isActive);
      chip.setAttribute("aria-pressed", String(isActive));
    });

    this.updatePrice(price);
    this.updateRender();
    this.updateSummarySwatches();
    this.updateAtcBtn();
  }

  /* =====================================================================
     RENDER LOOKUP + IMAGE SWAP
  ===================================================================== */

  /** Returns the CDN filename prefix for the current product: "balu-sofa_" or "" */
  getRenderPrefix() {
    const handle = this.data.productHandle || "";
    return handle ? `${handle}_` : "";
  }

  getRenderUrl() {
    const { fabricSlug, colourSlug, layer2 } = this.state;
    if (!fabricSlug || !colourSlug || !layer2) return null;
    return `${this.data.cdnBase || ""}${this.getRenderPrefix()}${fabricSlug}_${colourSlug}_${layer2}.jpg`;
  }

  getPanelRenderUrl() {
    const { fabricSlug, colourSlug, layer2 } = this.state;
    if (!fabricSlug || !colourSlug || !layer2) return null;
    return `${this.data.cdnBase || ""}${this.getRenderPrefix()}${fabricSlug}_${colourSlug}_${layer2}_panel.jpg`;
  }

  updateRender() {
    const renderUrl = this.getRenderUrl();
    const panelUrl = this.getPanelRenderUrl();
    if (!renderUrl) {
      this.hideRender();
      return;
    }

    // Store full-size URL for fullscreen — panel uses the smaller version
    this.currentRenderUrl = renderUrl;

    if (!this.summaryRenderImg) return;

    // Hide everything while the new image loads — revealed only once onload/onerror fires
    this.summaryRenderImg.style.display = "none";
    if (this.viewRenderBtn) this.viewRenderBtn.style.display = "none";
    if (this.renderComingSoon) this.renderComingSoon.style.display = "none";

    this.summaryRenderImg.onerror = () => {
      this.summaryRenderImg.style.display = "none";
      if (!this.renderComingSoon) {
        this.renderComingSoon = document.createElement("p");
        this.renderComingSoon.className = "cfg-panel__render-coming-soon caption-plaid";
        this.renderComingSoon.textContent = "Render will be available soon.";
        this.summaryRenderImg.parentElement.appendChild(this.renderComingSoon);
      }
      this.renderComingSoon.style.display = "block";
    };
    this.summaryRenderImg.onload = () => {
      this.summaryRenderImg.style.display = "block";
      if (this.renderComingSoon) this.renderComingSoon.style.display = "none";
      if (this.viewRenderBtn) {
        this.viewRenderBtn.style.display = "block";
        this.viewRenderBtn.textContent = "View Full Screen";
      }
    };
    // Force eager so the browser loads the image even while display:none
    this.summaryRenderImg.loading = "eager";
    this.summaryRenderImg.src = panelUrl;
  }

  hideRender() {
    this.currentRenderUrl = null;
    if (this.summaryRenderImg) {
      this.summaryRenderImg.onerror = null;
      this.summaryRenderImg.onload = null;
      this.summaryRenderImg.style.display = "none";
      this.summaryRenderImg.src = "";
    }
    if (this.viewRenderBtn) {
      this.viewRenderBtn.style.display = "none";
      this.viewRenderBtn.textContent = "View Full Screen";
    }
    if (this.renderComingSoon) this.renderComingSoon.style.display = "none";
  }

  /* =====================================================================
     PRICE
  ===================================================================== */

  updatePrice(priceInCents) {
    const formatted = this.formatMoney(priceInCents);
    if (this.pagePrice) this.pagePrice.textContent = formatted;
    if (this.summaryPrice) this.summaryPrice.textContent = formatted;
  }

  formatMoney(cents) {
    const symbol = this.data.currency || "$";
    const amount = cents / 100;
    // Show 2 decimal places only if there are cents
    const formatted = Number.isInteger(amount)
      ? amount.toLocaleString("en-GB")
      : amount.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    return `${symbol}${formatted}`;
  }

  /* =====================================================================
     SUMMARY SWATCHES
  ===================================================================== */

  updateSummarySwatches() {
    if (!this.summarySwatches) return;

    const { fabricSlug, colourSlug, layer2 } = this.state;
    const layer2Label =
      this.layer2Options.find((o) => this.slugify(o) === layer2) || layer2;
    let html = "";

    // Wood texture tile — shown as soon as a wood (layer2) is set
    if (this.data.cdnBase && layer2) {
      const woodUrl = `${this.data.cdnBase}wood_${layer2}.jpg`;
      html += `<div class="cfg-panel__summary-swatch">
        <img src="${woodUrl}" alt="${layer2Label} finish" class="cfg-panel__summary-swatch-tile" onerror="this.parentElement.style.display='none'">
        <span class="cfg-panel__summary-swatch-caption caption-plaid">${layer2Label}</span>
      </div>`;
    }

    // Fabric swatch tile — only shown once a colour is explicitly selected
    if (fabricSlug && colourSlug) {
      const group = this.fabricGroups.get(fabricSlug);
      const selectedColour = group?.colours.find((c) => c.colourSlug === colourSlug);
      if (selectedColour?.swatchImage) {
        html += `<div class="cfg-panel__summary-swatch">
          <img src="${selectedColour.swatchImage}" alt="${group.fabricName}" class="cfg-panel__summary-swatch-tile" onerror="this.parentElement.style.display='none'">
          <span class="cfg-panel__summary-swatch-caption caption-plaid">${group.fabricName} — ${selectedColour.colourName}</span>
        </div>`;
      }
    }

    this.summarySwatches.innerHTML = html ||
      `<span class="cfg-panel__summary-swatch-empty caption-plaid">Select material to preview</span>`;
  }

  /* =====================================================================
     ADD TO CART
  ===================================================================== */

  updateAtcBtn() {
    if (!this.atcBtn) return;
    const ready = !!(
      this.state.variantId &&
      this.state.layer2 &&
      this.state.fabricSlug &&
      this.state.colourSlug
    );
    this.atcBtn.disabled = !ready;
    this.atcBtn.setAttribute("aria-disabled", String(!ready));
  }

  addToCart(e) {
    if (!this.state.variantId) {
      e?.preventDefault();
      return;
    }

    const group = this.fabricGroups.get(this.state.fabricSlug);
    const colour = group?.colours.find(
      (c) => c.colourSlug === this.state.colourSlug,
    );

    // Selected layer 1 display label
    const layer1Display =
      this.state.layer1 === "all"
        ? "All"
        : this.layer1Options.find(
            (o) => this.slugify(o) === this.state.layer1,
          ) || this.state.layer1;

    // Selected layer 2 display label
    const layer2Display =
      this.layer2Options.find((o) => this.slugify(o) === this.state.layer2) ||
      this.state.layer2;

    if (this.variantInput) this.variantInput.value = this.state.variantId;
    if (this.propLayer1) this.propLayer1.value = layer1Display;
    if (this.propLayer2) this.propLayer2.value = layer2Display;
    if (this.propFabric) this.propFabric.value = group?.fabricName || "";
    if (this.propColour) this.propColour.value = colour?.colourName || "";

    // Swatch image URLs for cart display
    const materialSwatchUrl =
      this.data.cdnBase && this.state.layer2
        ? `${this.data.cdnBase}wood_${this.state.layer2}.jpg`
        : "";
    const fabricSwatchUrl = colour?.swatchImage || "";
    if (this.propMaterialSwatch) this.propMaterialSwatch.value = materialSwatchUrl;
    if (this.propFabricSwatch) this.propFabricSwatch.value = fabricSwatchUrl;
    if (this.propRenderImage) this.propRenderImage.value = this.getRenderUrl() || "";

    this.closePanel();
  }

  /* =====================================================================
     PANEL OPEN / CLOSE
  ===================================================================== */

  openPanel() {
    this.panel?.classList.add("is-open");
    this.overlay?.classList.add("is-open");
    document.body.classList.add("cfg-panel-open");
    this.openBtn?.setAttribute("aria-expanded", "true");
    this.panel?.removeAttribute("aria-hidden");
    this.closeBtn?.focus();
  }

  closePanel() {
    this.panel?.classList.remove("is-open");
    this.overlay?.classList.remove("is-open");
    document.body.classList.remove("cfg-panel-open");
    this.openBtn?.setAttribute("aria-expanded", "false");
    this.panel?.setAttribute("aria-hidden", "true");
    this.openBtn?.focus();
  }

  /* =====================================================================
     FULL SCREEN
  ===================================================================== */

  openFullscreen() {
    if (!this.currentRenderUrl || !this.fullscreenEl) return;

    // Show immediately with the panel image (already loaded) as a placeholder
    if (this.fullscreenRender) {
      this.fullscreenRender.src = this.getPanelRenderUrl();
    }

    this.fullscreenEl.hidden = false;
    document.body.classList.add("cfg-fullscreen-open");

    // Load the full-size image in the background, swap in when ready
    const fullImg = new Image();
    fullImg.onload = () => {
      if (this.fullscreenRender) {
        this.fullscreenRender.src = this.currentRenderUrl;
      }
    };
    fullImg.src = this.currentRenderUrl;
  }

  closeFullscreen() {
    if (!this.fullscreenEl) return;
    this.fullscreenEl.hidden = true;
    document.body.classList.remove("cfg-fullscreen-open");
  }

  /* =====================================================================
     FABRIC INFO TABS
  ===================================================================== */

  setFabricTab(tabKey) {
    this.fabricTabs.forEach((tab) => {
      const isActive = tab.dataset.fabricTab === tabKey;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
    });
    this.fabricTabPanels.forEach((panel) => {
      panel.classList.toggle("active", panel.dataset.fabricPanel === tabKey);
    });
  }

  /* =====================================================================
     INFO BLOCK — ACCORDION
  ===================================================================== */

  initInfoAccordion() {
    const accordion = document.querySelector("[data-cfg-accordion]");
    if (!accordion) return;

    const items = [...accordion.querySelectorAll("[data-cfg-accordion-item]")];
    items.forEach((item) => {
      const trigger = item.querySelector("[data-cfg-accordion-trigger]");
      if (!trigger) return;
      trigger.addEventListener("click", () => {
        const isOpen = item.classList.contains("is-open");
        // Close all siblings (exclusive accordion)
        items.forEach((i) => {
          i.classList.remove("is-open");
          const t = i.querySelector("[data-cfg-accordion-trigger]");
          if (t) t.setAttribute("aria-expanded", "false");
        });
        if (!isOpen) {
          item.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
        }
      });
    });
  }

  /* =====================================================================
     INFO BLOCK — NAV SCROLL + ACTIVE STATE
  ===================================================================== */

  initInfoNav() {
    if (!this.navLinks || !this.navLinks.length) return;

    const sections = document.querySelectorAll(".cfg-info__section");
    if (!sections.length) return;

    // Click handler — smooth scroll to target section
    this.navLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const target = document.querySelector(link.getAttribute("href"));
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    // IntersectionObserver to track active section
    const rootMarginTop = getComputedStyle(document.documentElement)
      .getPropertyValue("--headerHeight").trim() || "68px";
    const observerOptions = {
      rootMargin: `-${rootMarginTop} 0px -60% 0px`,
      threshold: 0,
    };

    this.infoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          this.navLinks.forEach((link) => {
            link.classList.toggle("active", link.getAttribute("href") === `#${id}`);
          });
        }
      });
    }, observerOptions);

    sections.forEach((section) => this.infoObserver.observe(section));
  }

  /* =====================================================================
     EVENT BINDING
  ===================================================================== */

  bindEvents() {
    this.openBtn?.addEventListener("click", () => this.openPanel());
    this.closeBtn?.addEventListener("click", () => this.closePanel());
    document.getElementById("cfg-close-panel-mobile")?.addEventListener("click", () => this.closePanel());
    this.overlay?.addEventListener("click", () => this.closePanel());

    // View Product button hidden until a valid render URL is available
    if (this.viewRenderBtn) {
      this.viewRenderBtn.style.display = "none";
      this.viewRenderBtn.addEventListener("click", () => this.openFullscreen());
    }

    this.fullscreenCloseBtn?.addEventListener("click", () =>
      this.closeFullscreen(),
    );

    this.zoomBtn?.addEventListener("click", () => {
      const activeIndex = this.heroSwiper ? this.heroSwiper.activeIndex : 0;
      window.imageZoomModal?.open(activeIndex);
    });

    this.cartForm?.addEventListener("submit", (e) => this.addToCart(e));

    this.fabricTabs.forEach((tab) => {
      tab.addEventListener("click", () =>
        this.setFabricTab(tab.dataset.fabricTab),
      );
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (!this.fullscreenEl?.hidden) this.closeFullscreen();
      else if (this.panel?.classList.contains("is-open")) this.closePanel();
    });
  }

  /* =====================================================================
     UTILITIES
  ===================================================================== */

  /** Converts a display label to a URL/lookup slug: "Brown Maple" → "brown-maple" */
  slugify(str) {
    if (!str) return "";
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  /** Creates a pill button element */
  makePill(label, value, isActive) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `variant-pill cfg-pill cfg-l1-pill caption-plaid${isActive ? " active" : ""}`;
    btn.dataset.value = value;
    btn.textContent = label;
    btn.setAttribute("aria-pressed", String(isActive));
    return btn;
  }
}

/* ---- Init ---- */
document.addEventListener("DOMContentLoaded", () => {
  new ProductConfigurator();
});
